import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiPlus,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiImage,
  FiUpload,
  FiX,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { galleryAPI } from "../../services/api";
import { resolveImageUrl } from "../../utils/helpers";
import { triggerRefresh } from "../../hooks/useDataRefresh";
import AdminLayout from "../../components/layout/AdminLayout";
import Modal from "../../components/common/Modal";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const CATEGORIES = [
  "Campus",
  "Events",
  "Sports",
  "Academic",
  "Cultural",
  "Other",
];

const gradients = [
  "from-blue-400 to-blue-600",
  "from-green-400 to-green-600",
  "from-purple-400 to-purple-600",
  "from-primary-400 to-primary-600",
  "from-secondary-400 to-secondary-600",
  "from-red-400 to-red-600",
];

const emptyForm = {
  title: "",
  description: "",
  category: "Events",
  image: null,
};

const emptyBulkForm = {
  category: "Events",
  images: [],
};

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [bulkModal, setBulkModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [bulkForm, setBulkForm] = useState(emptyBulkForm);
  const [bulkPreviews, setBulkPreviews] = useState([]);
  const [bulkImageMetadata, setBulkImageMetadata] = useState({});
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await galleryAPI.getAdmin();
        setImages(res.data?.data?.images || []);
      } catch (err) {
        toast.error("Failed to load gallery images");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((p) => ({ ...p, image: file }));
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleBulkFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setBulkForm((p) => ({ ...p, images: files }));

    // Create previews and initialize metadata for all selected files
    const previews = [];
    const metadata = {};
    let loadedCount = 0;

    files.forEach((file, idx) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        previews.push({
          file,
          preview: ev.target.result,
        });
        // Initialize metadata with filename-based title
        const fileNameWithoutExt = file.name.split(".").slice(0, -1).join(".");
        metadata[idx] = {
          title: fileNameWithoutExt || `Photo ${idx + 1}`,
          description: "",
        };
        loadedCount++;
        if (loadedCount === files.length) {
          setBulkPreviews(previews);
          setBulkImageMetadata(metadata);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.image) {
      toast.error("Please select an image");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v) fd.append(k, v);
      });
      const res = await galleryAPI.upload(fd);
      const newItem = res.data?.data?.image;
      if (newItem) setImages((prev) => [newItem, ...prev]);
      toast.success("Image uploaded successfully");
      setModal(false);
      setForm(emptyForm);
      setPreview(null);
      triggerRefresh("gallery"); // Update all gallery sections
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        "Failed to upload image. Check your Cloudinary settings.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkUpload = async () => {
    if (bulkForm.images.length === 0) {
      toast.error("Please select at least one image");
      return;
    }

    if (!bulkForm.title?.trim()) {
      toast.error("Please enter a title for the images");
      return;
    }

    setBulkSaving(true);
    try {
      const fd = new FormData();
      bulkForm.images.forEach((img) => {
        fd.append("images", img);
      });
      fd.append("title", bulkForm.title);
      fd.append("category", bulkForm.category);

      const res = await galleryAPI.uploadBulk(fd);
      const newItems = res.data?.data?.images || [];
      const failedItems = res.data?.data?.failed || [];

      // Reload all gallery images to ensure nothing is lost
      const allRes = await galleryAPI.getAdmin();
      const allImages = allRes.data?.data?.images || [];
      setImages(allImages);

      if (failedItems.length > 0) {
        const failedNames = failedItems.map((f) => f.fileName).join(", ");
        toast.warning(
          `${newItems.length} uploaded, ${failedItems.length} failed: ${failedNames}`,
        );
      } else {
        toast.success(`${newItems.length} image(s) uploaded successfully`);
      }
      setBulkModal(false);
      setBulkForm(emptyBulkForm);
      setBulkPreviews([]);
      setBulkImageMetadata({});
      triggerRefresh("gallery"); // Update all gallery sections
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        "Failed to upload images. Check your Cloudinary settings.";
      toast.error(msg);
    } finally {
      setBulkSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await galleryAPI.togglePublish(id);
      setImages((prev) =>
        prev.map((i) =>
          i._id === id ? { ...i, isPublished: !i.isPublished } : i,
        ),
      );
      toast.success("Status updated");
      triggerRefresh("gallery"); // Update all gallery sections
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    try {
      await galleryAPI.delete(id);
      setImages((prev) => prev.filter((i) => i._id !== id));
      setDeleteConfirm(null);
      toast.success("Image deleted");
      triggerRefresh("gallery"); // Update all gallery sections
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete image");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedForDelete.size === 0) {
      toast.error("Please select at least one image to delete");
      return;
    }

    setSaving(true);
    try {
      const ids = Array.from(selectedForDelete);
      for (const id of ids) {
        await galleryAPI.delete(id);
      }
      setImages((prev) => prev.filter((i) => !selectedForDelete.has(i._id)));
      setSelectedForDelete(new Set());
      toast.success(`${ids.length} image(s) deleted successfully`);
      triggerRefresh("gallery");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete images");
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectImage = (id) => {
    const newSelected = new Set(selectedForDelete);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedForDelete(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedForDelete.size === images.length) {
      setSelectedForDelete(new Set());
    } else {
      setSelectedForDelete(new Set(images.map((i) => i._id)));
    }
  };

  const catColors = {
    Campus: "bg-blue-100 text-blue-700",
    Events: "bg-green-100 text-green-700",
    Sports: "bg-orange-100 text-orange-700",
    Academic: "bg-purple-100 text-purple-700",
    Cultural: "bg-pink-100 text-pink-700",
    Other: "bg-gray-100 text-gray-700",
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-serif font-bold text-primary-500">
            Gallery Management
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Upload and manage school photos
          </p>
        </div>
        <div className="flex gap-3">
          {selectedForDelete.size > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors text-sm disabled:opacity-50"
            >
              <FiTrash2 className="w-4 h-4" /> Delete ({selectedForDelete.size})
            </button>
          )}
          <button
            onClick={() => {
              setBulkForm(emptyBulkForm);
              setBulkPreviews([]);
              setBulkModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-xl font-medium hover:bg-secondary-600 transition-colors text-sm"
          >
            <FiUpload className="w-4 h-4" /> Upload Multiple
          </button>
          <button
            onClick={() => {
              setForm(emptyForm);
              setPreview(null);
              setModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors text-sm"
          >
            <FiPlus className="w-4 h-4" /> Upload Image
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div>
          {images.length > 0 && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-xl">
              <input
                type="checkbox"
                checked={
                  images.length > 0 && selectedForDelete.size === images.length
                }
                onChange={toggleSelectAll}
                className="w-5 h-5 cursor-pointer rounded border-gray-300"
                title="Select all images"
              />
              <span className="text-sm text-gray-600">
                {selectedForDelete.size > 0
                  ? `${selectedForDelete.size} selected`
                  : "Select multiple images to delete them"}
              </span>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, idx) => (
              <motion.div
                key={image._id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
                className={`bg-white border rounded-2xl overflow-hidden shadow-sm group relative transition-all ${
                  selectedForDelete.has(image._id)
                    ? "border-red-400 bg-red-50"
                    : "border-gray-100"
                }`}
              >
                {/* Selection Checkbox */}
                <div className="absolute top-2 left-2 z-10">
                  <input
                    type="checkbox"
                    checked={selectedForDelete.has(image._id)}
                    onChange={() => toggleSelectImage(image._id)}
                    className="w-5 h-5 cursor-pointer rounded border-gray-300"
                  />
                </div>

                {/* Image */}
                <div className="relative h-40 overflow-hidden">
                  {image.imageUrl ? (
                    <img
                      src={resolveImageUrl(image.imageUrl)}
                      alt={image.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className={`w-full h-full bg-gradient-to-br ${gradients[idx % gradients.length]} flex items-center justify-center`}
                    >
                      <FiImage className="w-10 h-10 text-white/30" />
                    </div>
                  )}
                  {/* Overlay actions */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => handleToggle(image._id)}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors backdrop-blur-sm"
                      title={image.isPublished ? "Unpublish" : "Publish"}
                    >
                      {image.isPublished ? (
                        <FiEyeOff className="w-4 h-4" />
                      ) : (
                        <FiEye className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(image._id)}
                      className="p-2 bg-red-500/80 hover:bg-red-600 rounded-full text-white transition-colors"
                      title="Delete"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Published badge */}
                  <div className="absolute top-2 right-2">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${image.isPublished ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}
                    >
                      {image.isPublished ? "Live" : "Draft"}
                    </span>
                  </div>
                </div>

                <div className="p-3">
                  <h3 className="font-medium text-gray-800 text-sm truncate mb-1">
                    {image.title}
                  </h3>
                  <div className="flex items-center justify-between">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${catColors[image.category] || catColors.Other}`}
                    >
                      {image.category}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleToggle(image._id)}
                        className={`p-1 rounded-lg transition-colors ${image.isPublished ? "text-orange-400 hover:bg-orange-50" : "text-green-400 hover:bg-green-50"}`}
                      >
                        {image.isPublished ? (
                          <FiEyeOff className="w-3.5 h-3.5" />
                        ) : (
                          <FiEye className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(image._id)}
                        className="p-1 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            {images.length === 0 && (
              <div className="col-span-full text-center py-20">
                <FiImage className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No images in gallery</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <Modal
        isOpen={modal}
        onClose={() => setModal(false)}
        title="Upload Image"
        size="md"
        footer={
          <>
            <button
              onClick={() => setModal(false)}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 disabled:opacity-70"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <FiUpload className="w-4 h-4" />
              )}
              Upload
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Image preview / Upload area */}
          <div>
            {preview ? (
              <div className="relative w-full h-48 rounded-2xl overflow-hidden border border-gray-200">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => {
                    setPreview(null);
                    setForm((p) => ({ ...p, image: null }));
                  }}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  <FiX className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:border-primary-400 hover:bg-primary-50/30 transition-colors">
                <FiUpload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-gray-500 text-sm">
                  Click to upload image
                </span>
                <span className="text-gray-400 text-xs mt-1">
                  JPG, PNG, WebP up to 5MB
                </span>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  accept="image/*"
                />
              </label>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              placeholder="Image title"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={form.category}
              onChange={(e) =>
                setForm((p) => ({ ...p, category: e.target.value }))
              }
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white"
            >
              {CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
              rows={2}
              placeholder="Optional description..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none resize-none"
            />
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        size="sm"
        footer={
          <>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDelete(deleteConfirm)}
              className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600 text-sm">
          Are you sure you want to delete this image? This action cannot be
          undone.
        </p>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        isOpen={bulkModal}
        onClose={() => setBulkModal(false)}
        title="Upload Multiple Images"
        size="lg"
        footer={
          <>
            <button
              onClick={() => setBulkModal(false)}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkUpload}
              disabled={bulkSaving}
              className="flex items-center gap-2 px-4 py-2 bg-secondary-500 text-white rounded-xl text-sm font-medium hover:bg-secondary-600 disabled:opacity-70"
            >
              {bulkSaving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <FiUpload className="w-4 h-4" />
              )}
              Upload{" "}
              {bulkForm.images.length > 0 ? `(${bulkForm.images.length})` : ""}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Images upload area */}
          <div>
            {bulkPreviews.length > 0 ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Selected Images ({bulkPreviews.length})
                </label>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {bulkPreviews.map((item, idx) => (
                    <div
                      key={idx}
                      className="relative h-24 rounded-lg overflow-hidden border border-gray-200"
                    >
                      <img
                        src={item.preview}
                        alt={`Preview ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => {
                          const newImages = bulkForm.images.filter(
                            (_, i) => i !== idx,
                          );
                          const newPreviews = bulkPreviews.filter(
                            (_, i) => i !== idx,
                          );
                          setBulkForm((p) => ({ ...p, images: newImages }));
                          setBulkPreviews(newPreviews);
                        }}
                        className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <FiX className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:border-secondary-400 hover:bg-secondary-50/30 transition-colors">
              <FiUpload className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-gray-500 text-sm">
                Click to select images
              </span>
              <span className="text-gray-400 text-xs mt-1">
                JPG, PNG, WebP up to 5MB (max 50 images)
              </span>
              <input
                type="file"
                multiple
                onChange={handleBulkFileChange}
                className="hidden"
                accept="image/*"
              />
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title (applies to all images)
            </label>
            <input
              type="text"
              placeholder="e.g., Sports Day 2025, Annual Festival"
              value={bulkForm.title || ""}
              onChange={(e) =>
                setBulkForm((p) => ({ ...p, title: e.target.value }))
              }
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-secondary-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={bulkForm.category}
              onChange={(e) =>
                setBulkForm((p) => ({ ...p, category: e.target.value }))
              }
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white"
            >
              {CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p className="text-sm text-blue-700">
              💡 <strong>Tip:</strong> Upload multiple photos at once. All
              images will use the same title. You can delete individual images
              anytime from the gallery if needed.
            </p>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default AdminGallery;
